import _ from 'lodash';

import { useChainId } from '@occc-app/Chain/hooks';
import {
  Fuel_And_Convenience_Profile_Attribute,
  RetailerNetworkPartialFragment,
} from '@occc/graphql';

import { useRetailerNetworksForUser } from '@occc/feat-retailer-connect';
import { useConfig } from '@occc/config';
import { groupAudienceTargetingAttributesByType, isNotUndefined } from './utils';
import { useGetQualifiedEventsTargetingAttribute } from './hooks/useGetQualifiedEventsTargetingAttribute';
import { useGetAudienceById } from './useGetAudienceById';
import { useGetAudienceProductGroupsAndSegmentsByIds } from './useGetAudienceProductGroupsAndSegmentsByIds';
import { prepareSurveyAttributes } from './prepareSurveyAttributes';
import { prepareAudienceSegmentAttributes } from './prepareAudienceSegmentAttributes';
import { prepareAudiencePurchaseAttributes } from './prepareAudiencePurchaseAttributes';
import { useGetAudienceSelectItemsByUuids } from './useGetAudienceSelectItemsByUuids';

import { useGetProductCategoriesByIds } from '../productCategory/queries';
import { useGetOfferByIds } from '../offer/queries';
import { useGetLocationsQuery } from '../location/queries';
import { useGetLocationGroupsByUuids } from '../locationGroup/queries';
import { useGetBasicInfoWalletsByIds } from '../steeringWallet/queries';
import { useGetGeneralSurveys } from '../survey/queries';
import { formValuesByTargetingTypename as formValuesByProductInterestsTargetingTypename } from '../../routes/Audience/components/ProductInterests/constants';
import { EmployeeTargetingAttributes } from '../../routes/Audience/types';

const RETAILER_NETWORKS_FOR_USER_LIMIT = 300;

export const useGetAudienceWithDataById = (audienceId: string, chainIdEquals?: string) => {
  const chainId = useChainId();

  const {
    loading: audienceLoading,
    error: audienceError,
    data: audience,
    loaded: audienceLoaded,
  } = useGetAudienceById(audienceId, chainIdEquals);
  const { config } = useConfig();

  const groupAttributes = groupAudienceTargetingAttributesByType(audience);

  const shouldFilterOutACH =
    !config.ach.enabled && groupAttributes.fuelAndConvenienceProfile?.[0]?.criteria?.length;
  if (shouldFilterOutACH) {
    groupAttributes.fuelAndConvenienceProfile[0] = {
      ...groupAttributes.fuelAndConvenienceProfile[0],
      criteria: groupAttributes.fuelAndConvenienceProfile[0].criteria.filter(
        ({ attribute }) => attribute !== Fuel_And_Convenience_Profile_Attribute.HasAch,
      ),
    };
  }

  const audienceProductGroupIds = _.flatten(
    groupAttributes.purchases.map((item) => item.productGroupIds || []),
  );
  const audienceSegmentIds = _.flatten(groupAttributes.segments.map((item) => item.segmentIds));
  const offerIds = groupAttributes?.fulfillments.map((fulfillment) => {
    return fulfillment.targetOfferId;
  });

  const productCategoryIds = groupAttributes.productInterests?.[0]?.productCategories ?? [];

  const { data: productCategories } = useGetProductCategoriesByIds(productCategoryIds);

  const { data: retailerNetworks } = useRetailerNetworksForUser(RETAILER_NETWORKS_FOR_USER_LIMIT); // TODO: refactor when OCST-12908 done

  const { data: offers } = useGetOfferByIds(offerIds, chainId, {
    skip: !offerIds.length,
  });

  const { data: homeStoreLocations } = useGetLocationsQuery({
    skip: !groupAttributes?.homeStore?.[0]?.locationsIds,
    variables: {
      filter: {
        uuidContains: groupAttributes?.homeStore?.[0]?.locationsIds,
      },
    },
  });

  const { data: homeStoreLocationGroups } = useGetLocationGroupsByUuids(
    groupAttributes?.homeStore?.[0]?.locationGroupUuids ?? [],
  );

  const purchaseBasedLocationUuids = new Set<string>(
    groupAttributes.purchases.flatMap(({ purchasedLocationUuids }) => purchasedLocationUuids || []),
  );

  const { data: purchaseBasedLocations } = useGetLocationsQuery({
    skip: purchaseBasedLocationUuids.size === 0,
    variables: {
      filter: {
        uuidContains: Array.from(purchaseBasedLocationUuids),
      },
    },
  });

  const purchaseBasedLocationGroupUuids = new Set<string>(
    groupAttributes.purchases.flatMap(
      ({ purchasedLocationGroupUuids }) => purchasedLocationGroupUuids || [],
    ),
  );

  const { data: purchaseBasedLocationGroups } = useGetLocationGroupsByUuids(
    Array.from(purchaseBasedLocationGroupUuids),
  );

  const qualifiedEventsConditions = groupAttributes.qualifiedEvents[0]?.conditions;
  const qualifiedEventsAttributes =
    useGetQualifiedEventsTargetingAttribute(qualifiedEventsConditions);

  const walletUuidList = groupAttributes.walletSteeringItems.map((item) => item.walletUuid);
  const { data: wallets } = useGetBasicInfoWalletsByIds(walletUuidList);

  const surveyUuidList = groupAttributes.surveyItems.map((item) => item.surveyUuid);
  const { data: surveys } = useGetGeneralSurveys({
    baseOptions: {
      skip: !surveyUuidList.length,
      variables: {
        filter: {
          uuidContains: surveyUuidList,
        },
      },
    },
  });

  const surveyBasedLocationUuids = new Set<string>(
    groupAttributes.surveyItems.flatMap(({ locationsUuids }) => locationsUuids || []),
  );

  const { data: surveyBasedLocations } = useGetLocationsQuery({
    skip: surveyBasedLocationUuids.size === 0,
    variables: {
      filter: {
        uuidContains: Array.from(surveyBasedLocationUuids),
      },
    },
  });

  const surveyBasedLocationGroupUuids = new Set<string>(
    groupAttributes.surveyItems.flatMap(({ locationGroupUuids }) => locationGroupUuids || []),
  );

  const { data: surveyBasedLocationGroups } = useGetLocationGroupsByUuids(
    Array.from(surveyBasedLocationGroupUuids),
  );

  const audienceUuids = groupAttributes.combinedAudiences?.combinedAudiencesTarget;
  const { data: audiences } = useGetAudienceSelectItemsByUuids({
    uuids: audienceUuids,
  });

  const {
    loading: audienceDetailsLoading,
    error: audienceDetailsError,
    data: audienceDetails,
    loaded: audienceDetailsLoaded,
  } = useGetAudienceProductGroupsAndSegmentsByIds({
    chainIdEquals,
    productGroupIds: audienceProductGroupIds,
    segmentUuids: audienceSegmentIds,
  });

  const prepareCombinedAudiencesAttribute = () => {
    if (!groupAttributes.combinedAudiences) {
      return null;
    }

    const combinedAudiences = audiences
      ? groupAttributes.combinedAudiences.combinedAudiencesTarget
          .map((uuid) => {
            return audiences.find((item) => item.uuid === uuid);
          })
          .filter(isNotUndefined)
      : [];

    return {
      operator: groupAttributes.combinedAudiences.combinedAudiencesOperator,
      target: groupAttributes.combinedAudiences.combinedAudiencesTarget,
      audiences: combinedAudiences,
    };
  };

  const prepareAudienceFulfillmentAttributes = () => {
    if (!offers?.length || groupAttributes.fulfillments.length === 0) {
      return [];
    }

    return groupAttributes.fulfillments.map((attribute) => {
      const offerId = attribute.targetOfferId;
      const offer = offers.find((offer) => offer.id === offerId);

      return {
        ...attribute,
        targetOffer: offer
          ? {
              id: offer.id,
              name: offer.name,
            }
          : null,
      };
    });
  };

  const prepareWalletSteeringAttributes = () => {
    return (
      groupAttributes.walletSteeringItems.map((attribute) => {
        const wallet = wallets?.find((item) => item.uuid === attribute.walletUuid);

        return {
          ...attribute,
          wallet,
        };
      }) || []
    );
  };

  const prepareProductInterests = () => {
    const attribute = groupAttributes.productInterests.find((attribute) => Boolean(attribute));

    if (attribute) {
      return {
        ...formValuesByProductInterestsTargetingTypename[attribute.__typename],
        productCategories,
      };
    }

    return null;
  };

  const employeeRetailerNetworks: RetailerNetworkPartialFragment[] = (
    retailerNetworks?.retailerNetworksForUser?.edges || []
  ).map(({ node }) => node);

  const employeeTargetingAttributes: EmployeeTargetingAttributes | null =
    groupAttributes.employeeTargetingAttributes &&
    groupAttributes.employeeTargetingAttributes.retailerNetworkUserType
      ? {
          retailerNetworkUserType:
            groupAttributes.employeeTargetingAttributes.retailerNetworkUserType,
          operator: groupAttributes.employeeTargetingAttributes.operator,
          retailerNetworks: employeeRetailerNetworks.filter(({ uuid }) =>
            (groupAttributes.employeeTargetingAttributes || {}).retailerNetworks?.includes(uuid),
          ),
        }
      : null;

  const targetingAttributes = {
    ...groupAttributes,
    employeeTargetingAttributes,
    purchases: prepareAudiencePurchaseAttributes(
      groupAttributes,
      audienceDetails.productGroups,
      purchaseBasedLocations,
      purchaseBasedLocationGroups,
    ),
    segments: prepareAudienceSegmentAttributes(groupAttributes, audienceDetails.segments),
    fulfillments: prepareAudienceFulfillmentAttributes(),
    homeStore: [
      {
        ...groupAttributes?.homeStore?.[0],
        locations: homeStoreLocations,
        locationGroups: homeStoreLocationGroups,
      },
    ],
    walletSteeringItems: prepareWalletSteeringAttributes(),
    productInterests: prepareProductInterests(),
    qualifiedEvents: qualifiedEventsAttributes,
    lastVisit: groupAttributes.lastVisit,
    pointBalance: groupAttributes.pointBalance,
    membershipCreated: groupAttributes.membershipCreated,
    surveyItems: prepareSurveyAttributes(
      groupAttributes.surveyItems,
      surveys,
      surveyBasedLocations,
      surveyBasedLocationGroups,
    ),
    combinedAudiences: prepareCombinedAudiencesAttribute(),
    externalIdentityVerification: groupAttributes.externalIdentityVerification,
  };

  return {
    loading: audienceLoading || audienceDetailsLoading,
    error: audienceError || audienceDetailsError,
    data: {
      ...audience,
      name: audience?.name,
      description: audience?.description,
      targetingAttributes,
    },
    loaded: audienceLoaded && audienceDetailsLoaded,
  };
};
